import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useMemo, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { DeviceSettingsControl } from './cards/device-settings';
import { unpack_validation } from 'z3k-parser/validation/types';
import { Box } from '@mui/material';
import { EthernetSettings } from './cards/ethernet-settings';
import { GsmSettings } from './cards/gsm-settings';
import { DeviceInfoCard } from './cards/device-info';
import { usePartialAppSelector } from 'shared/hooks/redux/hooks';
import { useBox } from 'shared/hooks/utils';
import { select_object_array_parts } from '../utils/utils';
import { EmailNotificationsAccordion } from './cards/notification-settings';
import { LocalSubnet } from './cards/local-subnet';
import { StockConfigsCard } from 'console/device-settings/z3k/components/main-settings/stock-configs';
import { Z3KSettings, Z3KSettingsEditor, } from 'console/device-settings/z3k/provider/types';
import { selectDevice } from 'entities/device/lib';
import { useGetSimInDevice } from 'console/sim/utils/select-sim';
import { SimSettings } from 'z3k-settings/components/main-settings/cards/sim-settings';
import { WifiCard } from 'z3k-settings/components/main-settings/cards/wifi-settings';
const notificationOptions = createSelector(select_object_array_parts('notifications', [
    'id',
    'name',
]), (notifications) => [{ name: 'Оповещения', options: notifications }]);
const emptyWifi = {
    netname: '',
    pass: '',
    is_enabled: true,
};
export function SettingsPage() {
    const { save_device_settings, device_id, device_type, hardware_type, device_spec, is_staff, service_view, } = useContext(Z3KSettings);
    const { config, validation_result, validation_warnings_result, replace_config: onChangeConfig, deviceSettings, } = useContext(Z3KSettingsEditor);
    const stationary_location = usePartialAppSelector(selectDevice, device_id)?.stationary_location;
    const optional_info = usePartialAppSelector(selectDevice, device_id)?.optional_info_about_device;
    const redux_device_name = usePartialAppSelector(selectDevice, device_id)?.name;
    const [device_name, setDeviceName] = useState(deviceSettings?.name ?? redux_device_name ?? '');
    const onChangeDeviceName = useCallback((value) => {
        if (value.length <= 60) {
            save_device_settings({ name: value });
            setDeviceName(value);
        }
    }, [save_device_settings, setDeviceName]);
    const onChangeLocation = useCallback((newLoc) => {
        save_device_settings({
            stationary_location: newLoc ? { loc: newLoc } : null,
        });
    }, [save_device_settings]);
    const location = useMemo(() => deviceSettings?.stationary_location !== undefined
        ? deviceSettings.stationary_location
        : stationary_location, [deviceSettings?.stationary_location, stationary_location]);
    const configBox = useBox(config);
    const onChangeTimezone = useCallback((timezone) => {
        onChangeConfig({ ...configBox.current, timezone });
        save_device_settings({ timezone: timezone });
    }, [save_device_settings]);
    const onChangeUsbPassword = useCallback((usbpassword) => onChangeConfig({ ...configBox.current, usbpassword }), []);
    const onWifiChange = useCallback((wifi_settings) => onChangeConfig({ ...configBox.current, wifi_settings }), []);
    const onWifiAccessPoint = useCallback((wifi_access_point) => onChangeConfig({ ...configBox.current, wifi_access_point }), []);
    const onLocalSubnetChange = useCallback((local_subnet_name) => onChangeConfig({ ...configBox.current, local_subnet_name }), []);
    const onGsmChange = useCallback((gsm_settings) => onChangeConfig({ ...configBox.current, gsm_settings }), []);
    const [billed_sim] = useGetSimInDevice(device_id);
    const unpackPropsWifi = useMemo(() => ({
        value: config?.wifi_settings ?? emptyWifi,
        validation_result: unpack_validation(validation_result, 'wifi_settings'),
        validation_warnings_result: unpack_validation(validation_warnings_result, 'wifi_settings'),
        onChange: onWifiChange,
    }), [
        config,
        validation_result,
        validation_warnings_result,
        unpack_validation,
        onWifiChange,
    ]);
    const unpackPropsAccessPoint = useMemo(() => config.wifi_access_point && {
        value: config.wifi_access_point,
        validation_result: unpack_validation(validation_result, 'wifi_access_point'),
        validation_warnings_result: unpack_validation(validation_warnings_result, 'wifi_access_point'),
        onChange: onWifiAccessPoint,
    }, [
        config,
        validation_result,
        validation_warnings_result,
        unpack_validation,
        onWifiChange,
    ]);
    return (_jsxs(Box, { sx: {
            '& .MuiFormLabel-root > .MuiGrid-root.MuiGrid-container': {
                minHeight: '30px',
            },
            '& > .MuiPaper-root': {
                mb: 3,
            },
            '& > .MuiCard-root.device-info': {
                my: 3,
            },
        }, px: 2, children: [_jsx(DeviceSettingsControl, { onChangeLocation: onChangeLocation, location: location?.loc ?? null, device_name: device_name, onChangeDeviceName: onChangeDeviceName, timezone: config.timezone, onChangeTimezone: onChangeTimezone, showServicePassword: service_view, service_password: config.usbpassword ?? '', service_password_validation_result: unpack_validation(validation_result, 'usbpassword'), onChangeServicePassword: onChangeUsbPassword, children: _jsxs("div", { children: [device_spec.network.has_ethernet && _jsx(EthernetSettings, {}), device_spec.network.has_wifi &&
                            config?.wifi_settings?.is_enabled && (_jsx(WifiCard, { wifiProps: unpackPropsWifi, accessPointProps: unpackPropsAccessPoint })), device_spec.network.has_gsm && (_jsx(SimSettings, { device_id: device_id })), device_spec.network.has_gsm &&
                            billed_sim == null &&
                            config.gsm_settings != null && (_jsx(GsmSettings, { value: config.gsm_settings, validation_result: unpack_validation(validation_result, 'gsm_settings'), validation_warnings_result: unpack_validation(validation_warnings_result, 'gsm_settings'), onChange: onGsmChange, notifications: notificationOptions(config) })), device_spec.has_email_notifications && (_jsx(EmailNotificationsAccordion, {})), config.local_subnet_name !== undefined &&
                            (device_spec.network.has_ethernet ||
                                device_spec.network.has_wifi) && (_jsx(LocalSubnet, { value: config.local_subnet_name ?? '', validation_result: unpack_validation(validation_result, 'local_subnet_name'), validation_warnings_result: unpack_validation(validation_warnings_result, 'local_subnet_name'), onChange: onLocalSubnetChange }))] }) }), _jsx(DeviceInfoCard, { serial: config.login, dev_type: config.device.name, hardware: config.device.hardware, firmware: config.device.firmware, device_id: device_id, optional_info: optional_info, is_staff: is_staff }), !Z3K_IS_USB_UTILITY &&
                ~~config.device?.firmware >= 103 &&
                service_view && (_jsx(StockConfigsCard, { device_type: device_type, hardware_type: hardware_type, device_id: device_id, current: config?.stockconfig ?? null }))] }));
}
SettingsPage.isError = (validation_result) => {
    return (!!unpack_validation(validation_result, 'device') ||
        !!unpack_validation(validation_result, 'usbpassword') ||
        !!unpack_validation(validation_result, 'gsm_settings') ||
        !!unpack_validation(validation_result, 'wifi_settings') ||
        !!unpack_validation(validation_result, 'local_subnet_name'));
};
SettingsPage.isWarning = (validation_warnings_result) => {
    return (!!unpack_validation(validation_warnings_result, 'device') ||
        !!unpack_validation(validation_warnings_result, 'gsm_settings') ||
        !!unpack_validation(validation_warnings_result, 'wifi_settings') ||
        !!unpack_validation(validation_warnings_result, 'local_subnet_name'));
};
SettingsPage.isShown = () => true;
